import { z } from 'zod';
import { CitationKind } from '../citations';
import { ChatFeedbackRating, ChatMessageStatus, ChatRole, LlmProvider } from '../enums';
import { LlmRegion } from '../scoring';

export const BASELINE_CHAT_PROMPT_VERSION = 'chat-v1';

export const ASSISTANT_REFUSAL = "I can only help with this household's application and HV assessment.";

export const CHAT_HOUSEHOLD_MERGED = 'household_merged';

export const ASSISTANT_ERROR_MESSAGES = {
  busy: 'Loom is busy, try again shortly.',
  blocked: "Loom couldn't answer that.",
  failed: 'Loom could not answer. Try again.',
} as const;

export const ASSISTANT_LIMIT_DEFAULTS = {
  questionMaxChars: 2_000,
  maxTurnsPerThread: 30,
  messagesPerHour: 60,
  messagesPerDay: 300,
  maxOutputTokens: 1_024,
  contextActivities: 50,
  contextTextChars: 20_000,
  providerTimeoutMs: 60_000,
  idleThreadDays: 90,
} as const;

export type AssistantLimitKey = keyof typeof ASSISTANT_LIMIT_DEFAULTS;

export const ASSISTANT_LIMIT_BOUNDS: Record<AssistantLimitKey, { min: number; max: number }> = {
  questionMaxChars: { min: 100, max: 10_000 },
  maxTurnsPerThread: { min: 1, max: 200 },
  messagesPerHour: { min: 1, max: 1_000 },
  messagesPerDay: { min: 1, max: 10_000 },
  maxOutputTokens: { min: 256, max: 8_192 },
  contextActivities: { min: 1, max: 500 },
  contextTextChars: { min: 1_000, max: 100_000 },
  providerTimeoutMs: { min: 5_000, max: 300_000 },
  idleThreadDays: { min: 7, max: 365 },
};

const limit = (key: AssistantLimitKey) => z.number().int().min(ASSISTANT_LIMIT_BOUNDS[key].min).max(ASSISTANT_LIMIT_BOUNDS[key].max);

export const AssistantLimits = z.object({
  questionMaxChars: limit('questionMaxChars'),
  maxTurnsPerThread: limit('maxTurnsPerThread'),
  messagesPerHour: limit('messagesPerHour'),
  messagesPerDay: limit('messagesPerDay'),
  maxOutputTokens: limit('maxOutputTokens'),
  contextActivities: limit('contextActivities'),
  contextTextChars: limit('contextTextChars'),
  providerTimeoutMs: limit('providerTimeoutMs'),
  idleThreadDays: limit('idleThreadDays'),
});
export type AssistantLimits = z.infer<typeof AssistantLimits>;

export const AssistantSettings = z.object({
  enabled: z.boolean(),
  provider: LlmProvider,
  model: z.string().trim().min(1).max(120),
  region: LlmRegion,
  activePromptVersion: z.string().trim().min(1).max(64),
  ...AssistantLimits.shape,
});
export type AssistantSettings = z.infer<typeof AssistantSettings>;

export const UpdateAssistantSettings = AssistantSettings.omit({ activePromptVersion: true }).partial();
export type UpdateAssistantSettings = z.infer<typeof UpdateAssistantSettings>;

export const ASSISTANT_ERROR_CODES = {
  questionTooLong: 'assistant_question_too_long',
  turnLimit: 'assistant_turn_limit',
  rateLimited: 'assistant_rate_limited',
  disabled: 'assistant_disabled',
} as const;

export const ASSISTANT_DISABLED_MESSAGE = 'Assistant is switched off by a super admin';

export const AssistantStatus = z.object({ enabled: z.boolean() });
export type AssistantStatus = z.infer<typeof AssistantStatus>;

export const AskChatInput = z.object({
  text: z.string().trim().min(1).max(ASSISTANT_LIMIT_BOUNDS.questionMaxChars.max),
});
export type AskChatInput = z.infer<typeof AskChatInput>;

export const StoredChatCitation = z.object({
  tag: z.string(),
  kind: CitationKind,
  refId: z.string().uuid().nullable(),
  date: z.string().nullable(),
});
export type StoredChatCitation = z.infer<typeof StoredChatCitation>;

export const ChatCitation = z.object({
  tag: z.string(),
  kind: CitationKind,
  target: z.string().nullable(),
  label: z.string(),
  available: z.boolean(),
});
export type ChatCitation = z.infer<typeof ChatCitation>;

export const ChatMessageDto = z.object({
  id: z.string().uuid(),
  role: ChatRole,
  text: z.string(),
  status: ChatMessageStatus,
  citations: z.array(ChatCitation),
  unverifiedQuotes: z.number().int(),
  unverifiedSpans: z.array(z.string()),
  assessmentId: z.string().uuid().nullable(),
  assessmentScore: z.number().int().nullable(),
  rating: ChatFeedbackRating.nullable(),
  createdAt: z.string(),
});
export type ChatMessageDto = z.infer<typeof ChatMessageDto>;

export const ChatThreadDto = z.object({
  id: z.string().uuid(),
  promptVersion: z.string(),
  createdAt: z.string(),
  lastMessageAt: z.string().nullable(),
});
export type ChatThreadDto = z.infer<typeof ChatThreadDto>;

export const ChatMergedInto = z.object({
  id: z.string().uuid(),
  familyLastName: z.string(),
});
export type ChatMergedInto = z.infer<typeof ChatMergedInto>;
export const ChatUsageWindow = z.object({
  limit: z.number().int(),
  remaining: z.number().int(),
  resetsAt: z.string().nullable(),
});
export type ChatUsageWindow = z.infer<typeof ChatUsageWindow>;

export const ChatLimitsView = z.object({
  questionMaxChars: z.number().int(),
  maxTurns: z.number().int(),
  turnsLeft: z.number().int(),
  hourly: ChatUsageWindow,
  daily: ChatUsageWindow,
});
export type ChatLimitsView = z.infer<typeof ChatLimitsView>;

export const ChatView = z.object({
  householdId: z.string().uuid(),
  stale: z.boolean(),
  mergedInto: ChatMergedInto.nullable(),
  thread: ChatThreadDto.nullable(),
  messages: z.array(ChatMessageDto),
  limits: ChatLimitsView,
});
export type ChatView = z.infer<typeof ChatView>;

export const CHAT_TEST_TURN_MAX_CHARS = 50_000;

export const ChatTestTurn = z.object({
  role: z.enum(['user', 'model']),
  text: z.string().min(1).max(CHAT_TEST_TURN_MAX_CHARS),
});
export type ChatTestTurn = z.infer<typeof ChatTestTurn>;

const alternates = (turns: readonly ChatTestTurn[]) => turns.length % 2 === 0 && turns.every((t, i) => t.role === (i % 2 === 0 ? 'user' : 'model'));

export const ChatPromptTestInput = z.object({
  householdId: z.string().uuid(),
  history: z
    .array(ChatTestTurn)
    .max(2 * ASSISTANT_LIMIT_BOUNDS.maxTurnsPerThread.max)
    .refine(alternates, 'History must be question and answer pairs')
    .default([]),
  question: AskChatInput.shape.text,
});
export type ChatPromptTestInput = z.infer<typeof ChatPromptTestInput>;

export const ChatTestAnswer = ChatMessageDto.pick({ text: true, status: true, citations: true, unverifiedQuotes: true, unverifiedSpans: true });
export type ChatTestAnswer = z.infer<typeof ChatTestAnswer>;

export const ChatPromptTestResult = z.object({
  promptVersion: z.string(),
  householdId: z.string().uuid(),
  stale: z.boolean(),
  answer: ChatTestAnswer,
  history: z.array(ChatTestTurn),
  limits: ChatLimitsView,
});
export type ChatPromptTestResult = z.infer<typeof ChatPromptTestResult>;

export const RATEABLE_CHAT_STATUSES: readonly ChatMessageStatus[] = ['ok', 'refused', 'truncated'];

export const isRateableMessage = (m: { role: ChatRole; status: ChatMessageStatus }): boolean => m.role === 'assistant' && RATEABLE_CHAT_STATUSES.includes(m.status);

export const FEEDBACK_COMMENT_MAX_CHARS = 1_000;

export const ChatFeedbackInput = z
  .object({
    rating: ChatFeedbackRating,
    comment: z.string().trim().max(FEEDBACK_COMMENT_MAX_CHARS).nullish(),
  })
  .refine((v) => v.rating === 'down' || !v.comment, { message: 'Only a thumbs-down takes a comment', path: ['comment'] });
export type ChatFeedbackInput = z.infer<typeof ChatFeedbackInput>;

export const AssistantFeedbackQuery = z
  .object({
    promptVersion: z.string().trim().min(1).max(64).optional(),
    rating: ChatFeedbackRating.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
  })
  .refine((v) => !v.from || !v.to || Date.parse(v.from) < Date.parse(v.to), { message: 'from must be before to', path: ['to'] });
export type AssistantFeedbackQuery = z.infer<typeof AssistantFeedbackQuery>;

export const AssistantFeedbackItem = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  rating: ChatFeedbackRating,
  comment: z.string().nullable(),
  questionText: z.string().nullable(),
  answerText: z.string().nullable(),
  promptVersion: z.string().nullable(),
  model: z.string().nullable(),
  householdId: z.string().uuid(),
  familyName: z.string(),
  authorName: z.string(),
});
export type AssistantFeedbackItem = z.infer<typeof AssistantFeedbackItem>;

export const AssistantFeedbackList = z.object({
  items: z.array(AssistantFeedbackItem),
  promptVersions: z.array(z.string()),
  truncated: z.boolean(),
});
export type AssistantFeedbackList = z.infer<typeof AssistantFeedbackList>;
